import { workflowXeroBillSchema } from "./workflow-posting.js";
import { z } from "zod";
import { channelArrangedWidgetSchema } from "./channel-widget-artifacts.js";
import { widgetResultSchema } from "./widget-results.js";
import { channelObjectFieldTypeSchema } from "./channel-field-types.js";
import { twinLogicFieldTypeSchema } from "./twin-logic.js";
import { workflowCorrectionPathSchema, workflowDocumentPathSchema } from "./workflow-document-paths.js";
import { workflowHistoryEntrySchema } from "./workflow-document-pattern.js";
import { workflowItemFailureSchema } from "./workflow-item-failure.js";
import { twinProposalObservationSchema, twinSourceRevisionsSchema } from "./twin-proposal-observations.js";
export * from "./twin-proposal-observations.js";
import { matchFeatureSchema, matchObservationRefSchema, matchOutcomeClassSchema, matchRunFreshnessSchema, } from "./twin-matching.js";
import { actionEvidenceSchema, actionSignalContextSchema, } from "./twin-actions.js";
/*
 * Twin proposals: one decision model for everything the Twin proposes.
 *
 * A proposal is one of four kinds. A link says two records are the same
 * entity; a definition is a governed view contract version the Twin authored; an
 * action is a prompt the Twin wants to run; a workflow review is an immutable
 * item assessment with no external effect. Each arrives in a batch from its
 * engine, carries the engine's confidence and band, and waits in one state
 * machine: accept, decline or later, with undo where the effect can be put
 * back. Decisions are append-only; a reversal is its own row; every effect
 * leaves a receipt. The engines stay separate. Deciding is one surface.
 */
const slugSchema = z.string().trim().regex(/^[a-z][a-z0-9_]{0,62}$/u);
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
const identifierSchema = z.string().trim().min(1).max(200);
const boundedText = (max) => z.string().trim().min(1).max(max);
export const TWIN_PROPOSAL_KINDS = ["link", "definition", "action", "workflow_review", "channel_widget_change"];
export const twinProposalKindSchema = z.enum(TWIN_PROPOSAL_KINDS);
/**
 * The kinds whose policy a person reads and sets. A widget change's is fixed (a person decides, and only an
 * organisation admin), so it is not among them, and clients that list these see the four they always have.
 */
export const TWIN_CONFIGURABLE_POLICY_KINDS = ["link", "definition", "action", "workflow_review"];
/**
 * Canonical names for Twin Build outputs. Proposal kinds remain
 * stable wire values; user-facing surfaces name the output that each lane builds.
 */
export const TWIN_BUILD_OUTPUT_TERMS = {
    link: {
        outputType: "entity_link",
        singular: "Entity link",
        plural: "Entity links",
        proposal: "Entity link proposal",
    },
    definition: {
        outputType: "view_contract",
        singular: "View contract",
        plural: "View contracts",
        proposal: "View contract proposal",
    },
    action: {
        outputType: "action",
        singular: "Action",
        plural: "Actions",
        proposal: "Action proposal",
    },
    workflow_review: {
        outputType: "workflow_review",
        singular: "Workflow review",
        plural: "Workflow reviews",
        proposal: "Workflow review proposal",
    },
    channel_widget_change: {
        outputType: "channel_widget_change",
        singular: "Widget change",
        plural: "Widget changes",
        proposal: "Widget change proposal",
    },
};
export const TWIN_PROPOSAL_STATES = [
    "open",
    "accepted",
    "declined",
    "deferred",
    "superseded",
    "expired",
    // Accepted, then stopped in one step by a Retire decision: no longer in effect, and not open to accept again.
    "retired",
];
export const twinProposalStateSchema = z.enum(TWIN_PROPOSAL_STATES);
export const TWIN_PROPOSAL_BANDS = ["automatic", "review", "abstain"];
export const twinProposalBandSchema = z.enum(TWIN_PROPOSAL_BANDS);
/** "retire" stops what an accepted proposal put in place; only kinds whose engine retires take it. */
export const TWIN_DECISIONS = ["accept", "decline", "retire"];
export const twinDecisionKindSchema = z.enum(TWIN_DECISIONS);
export const TWIN_EFFECT_CLASSES = ["reversible", "irreversible"];
export const twinEffectClassSchema = z.enum(TWIN_EFFECT_CLASSES);
export const TWIN_RECEIPT_STATUSES = ["applied", "failed", "completed"];
export const twinReceiptStatusSchema = z.enum(TWIN_RECEIPT_STATUSES);
export const TWIN_POLICY_MODES = ["ask", "auto"];
export const TWIN_ACCEPT_ROLES = ["member", "admin"];
/** Two records the matcher believes are the same entity. */
export const twinLinkPayloadSchema = z.object({
    entityType: slugSchema,
    left: matchObservationRefSchema,
    right: matchObservationRefSchema,
    features: z.record(z.string(), matchFeatureSchema),
    contradictions: z.array(slugSchema),
}).strict();
/** A governed view contract version an accepted authoring release produced. */
const twinDefinitionRelationshipEndSchema = z.object({
    relation: z.string().min(1).max(127),
    field: z.string().min(1).max(200),
}).strict();
/**
 * How many relationship candidates Twin Build's discovery may offer, and so how many links one definition card can
 * carry: a product gets one per selected candidate. Both limits are this one, because every stored card is parsed
 * against the contract whenever the Inbox loads, and a single card past its limit would stop the whole list.
 */
export const TWIN_DISCOVERY_CANDIDATE_MAX = 100;
/**
 * One link Twin Build found between two source tables and measured over the whole warehouse: `from`'s field holds
 * `to`'s, on `rows` rows of `from` with a value there, `matched` of which found theirs.
 */
export const twinDefinitionRelationshipSchema = z.object({
    kind: z.enum(["inclusion_dependency", "foreign_key", "equivalence", "containment", "reconciliation"]),
    cardinality: z.enum(["one_to_one", "many_to_one", "one_to_many", "many_to_many", "unknown"]),
    from: twinDefinitionRelationshipEndSchema,
    to: twinDefinitionRelationshipEndSchema,
    rows: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    /** The link as a person would say it: "Each Xero invoice belongs to one contact." */
    sentence: z.string().trim().min(1).max(300),
}).strict();
/** A source-records version a definition card puts in place with its product. */
export const twinDefinitionCarrierSchema = z.object({
    productId: identifierSchema,
    productVersionId: identifierSchema,
    productSha256: sha256Schema,
    productSlug: identifierSchema,
    productVersion: z.number().int().positive(),
    /** The records it holds, as the card names them: "Xero journal lines". */
    productName: z.string().trim().min(1).max(200),
    /** For a changed version, what changes ("Adds 1 field: Financial statement scope."); absent for new records. */
    changeSummary: z.string().trim().min(1).max(400).optional(),
}).strict();
export const twinDefinitionPayloadSchema = z.object({
    productId: identifierSchema,
    productVersionId: identifierSchema,
    productSha256: sha256Schema,
    productSlug: identifierSchema,
    /** "GitHub commit register", for the card: the relation a source-records product covers, else the slug in words. */
    productName: z.string().trim().min(1).max(200).optional(),
    /** The card's plain sentence: "Your invoices from Xero, kept up to date." Never names the system. */
    summary: z.string().trim().min(1).max(300).optional(),
    /** Up to three things a person can ask of the product, in their words. */
    questions: z.array(z.string().trim().min(1).max(200)).max(3).optional(),
    /** True when the product is live and this version replaces it; false for a new product. */
    update: z.boolean().optional(),
    /**
     * What accepting the card does, set by Twin Build when it raises the card (absent on earlier cards): `activates`
     * makes a new product one Twin answers from; `replaces` makes this version the one it answers from, in place of
     * `replacesVersion`, which stops being used.
     */
    onAccept: z.enum(["activates", "replaces"]).optional(),
    replacesVersion: z.number().int().positive().optional(),
    /** For an update: what changes for a person ("Nothing changes for you.", "Adds 2 fields: Due date, Customer."). */
    changeSummary: z.string().trim().min(1).max(400).optional(),
    productVersion: z.number().int().positive(),
    releaseId: identifierSchema,
    description: z.string().trim().max(4_000),
    population: z.string().trim().max(4_000),
    grain: z.string().trim().max(2_000),
    fieldCount: z.number().int().nonnegative(),
    /**
     * The version's fields in its own order against the live version of the same product, then the live fields it
     * drops; every field "added" when none is live. Absent on proposals raised before 2026-10-07.
     */
    fields: z.array(z.object({
        name: z.string().min(1).max(127),
        type: twinLogicFieldTypeSchema,
        change: z.enum(["added", "removed", "changed", "unchanged"]),
        /** Only the system needs it (scope, identifiers, deletion bookkeeping): the card shows it under Details. */
        technical: z.boolean().optional(),
    }).strict()).max(512).optional(),
    /** The warehouse tables the version reads, by name. Absent on proposals raised before 2026-10-07. */
    sources: z.array(z.string().min(1).max(127)).max(128).optional(),
    /**
     * The links between source tables the version is built on, as Twin Build found and measured them: counts only,
     * never rows. Absent on proposals raised before 2026-10-07.
     */
    relationships: z.array(twinDefinitionRelationshipSchema).max(TWIN_DISCOVERY_CANDIDATE_MAX).optional(),
    aliases: z.array(z.string()).max(32),
    activationPhrases: z.array(z.string()).max(64),
    catalogVisibility: z.enum(["public", "supporting"]).nullable(),
    supersedesProductSlugs: z.array(z.string()).max(64),
    dependencyProductSlugs: z.array(z.string()).max(64),
    /** Live products this one is a filtered copy of; absent when none and on earlier proposals. */
    narrowsProductSlugs: z.array(z.string()).max(64).optional(),
    exploratoryAccess: z.enum(["open", "discouraged", "blocked"]),
    /**
     * The new or changed source-records versions this product reads, which accepting it puts in place first: no person
     * reviews source records on their own, and they serve only the products that read them. Absent when the product
     * needs none, and on earlier proposals; a source-records version that matches the live one is never listed.
     */
    carriers: z.array(twinDefinitionCarrierSchema).min(1).max(16).optional(),
}).strict();
/** A prompt the Twin wants to run as the recipient, with the evidence it cites. */
export const twinActionPayloadSchema = z.object({
    title: boundedText(300),
    prompt: boundedText(2_000),
    rationale: z.string().trim().max(500),
    sourceKeys: z.array(z.string().trim().min(1)).max(40),
    model: boundedText(200),
    evidence: z.array(actionEvidenceSchema).default([]),
    proactiveContext: actionSignalContextSchema.nullable().default(null),
    proposerJobId: z.string().trim().min(1).max(200).nullable().default(null),
    edited: z.boolean().default(false),
}).strict();
/** Review of one immutable workflow item observation; accepting records a decision only. */
const workflowLineTargetSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("source_line"), sourceKey: identifierSchema,
        externalId: identifierSchema, subrecordKey: z.string().max(200).nullable() }).strict(),
    z.object({ kind: z.literal("document_line"), fileId: identifierSchema,
        lineKey: identifierSchema }).strict(),
]);
/** A question one of the workflow's own judge steps asked; the server resolves what Twin Judge answered. */
const workflowJudgmentRefSchema = z.object({ nodeId: identifierSchema,
    questionId: z.string().min(1).max(100),
    /** The answer the proposal rests on (an option, or yes/no); without it, whatever the judge answered. */
    expects: z.string().min(1).max(200).optional(),
    /**
     * The proposed value the question judged, from the finding's field: `lines[1].account_code` for one line's
     * account, `lines[1]` for the whole line. A review marks that value; without it the judgment is the finding's.
     */
    path: workflowCorrectionPathSchema.optional() }).strict();
/**
 * What Twin Judge answered for a finding, resolved by the server from the run's retained judgment, never
 * taken from the workflow's output. `choice` is the judge's answer; `expected`, the answer the proposal rests
 * on; `probability`, the judge's probability for that expected answer, so how strongly it supports the
 * proposal. Without an expected answer it is the judge's probability for its own.
 */
export const workflowFindingJudgmentSchema = z.object({
    source: z.enum(["workflow", "second_reader"]),
    /** The judge step that asked; null for the second reader, which is not a step of the workflow. */
    nodeId: identifierSchema.nullable(),
    questionId: z.string().min(1).max(100),
    question: z.string().max(500),
    choice: z.string().max(200),
    expected: z.string().max(200).optional(),
    probability: z.number().min(0).max(1),
    model: z.string().regex(/^jev-\d+\.\d+\.\d+$/u),
    /** The read whose established pattern an authored question named ({pattern:<key>}): its history answer. */
    patternKey: identifierSchema.optional(),
    /** The proposed value it judged (`lines[1].account_code`, `lines[1]`); without it, the finding as a whole. */
    path: workflowCorrectionPathSchema.optional(),
}).strict();
/** A finding as the workflow drafts it. */
/**
 * A field of a cited record that a finding rests on, so a review can circle it on the record. `evidenceRef` is one of
 * the finding's `evidenceRefs`, a record read (`ctx_…`) that selected `fieldKey`. The value is the record's own, from
 * the review's evidence, never the workflow's copy.
 */
export const workflowRecordFieldSchema = z.object({ evidenceRef: identifierSchema, fieldKey: identifierSchema }).strict();
/**
 * A piece of a finding's short reason; `mark` ties it to the finding's `recordFields` entry of that number (1-based).
 * A figure the workflow worked out rather than read (a count, a number of days) says how in `working`, which a review
 * opens from it: "Due 26 Jul 2026 and still unpaid on 7 Oct: 73 days." A piece is one or the other, not both.
 */
export const workflowReasonSegmentSchema = z.object({
    text: z.string().min(1).max(240),
    mark: z.number().int().min(1).max(9).optional(),
    working: z.string().trim().min(1).max(300).optional(),
}).strict().refine((segment) => !(segment.mark && segment.working), "A piece is read from a record or worked out, not both");
export const workflowReviewDraftFindingSchema = z.object({ fieldKey: identifierSchema,
    proposedValue: z.json(), rationale: z.string().trim().max(2000),
    evidenceRefs: z.array(identifierSchema).max(32),
    lineTarget: workflowLineTargetSchema.optional(),
    /** The extracted values this finding rests on, so a review can mark them on the document. */
    documentPaths: z.array(z.object({ evidenceRef: identifierSchema,
        path: workflowDocumentPathSchema }).strict()).max(32).optional(),
    /** The cited records' fields this finding rests on, numbered in order, so a review can mark them on the record. */
    recordFields: z.array(workflowRecordFieldSchema).max(9).optional(),
    /**
     * A short reason, read in order, whose facts carry the marks of `recordFields` (at most 240 characters in all);
     * without it a review shows `rationale`.
     */
    reason: z.array(workflowReasonSegmentSchema).min(1).max(6).optional(),
    /** The workflow's own judge-step questions this finding rests on; the second reader adds one more judgment. */
    judgedBy: z.array(workflowJudgmentRefSchema).max(15).optional(),
    /**
     * The values a reviewer may pick for the finding's field or, where it proposes lines, for a column of them (a
     * project, an account), by field or column key: a correction offers them as a list. An empty value is "none".
     */
    choices: z.record(identifierSchema, z.array(z.object({ value: z.string().trim().max(200), label: boundedText(200) })
        .strict()).min(1).max(60)).refine((choices) => Object.keys(choices).length <= 8, "Choices name at most eight fields")
        .optional() }).strict();
/**
 * What Accept writes to a record the run read, as the engine resolved it when it raised the card: each field's label
 * and type as the record's channel declares them, the value Accept sets and the value it had, which Undo restores.
 */
export const workflowRecordWriteSchema = z.object({
    /** The run's read of the record (a `ctx_…` observation), which the card cites. */
    evidenceRef: identifierSchema,
    channelId: identifierSchema,
    recordId: identifierSchema,
    recordTitle: z.string().max(300).nullable(),
    fields: z.array(z.object({ key: identifierSchema, label: boundedText(200), type: z.string().min(1).max(40),
        value: z.json(), previous: z.json() }).strict()).min(1).max(8),
}).strict();
/** One side of a card's pair, as people name it: where it is, what it is, and one short line of its facts. */
export const workflowReviewPairSideSchema = z.object({
    /** Where this side lives: "Uploaded document", "Xero bill", "New Xero bill". */
    source: boundedText(80),
    /** Its name: "R.C S.R.L Buongiorno 66535". */
    title: boundedText(200),
    /** One line of facts: "EUR 101.39 · 6 Sep · paid". */
    detail: boundedText(200).optional(),
    /** The run's read of it, one of the card's evidence references. */
    evidenceRef: identifierSchema.optional(),
}).strict();
/**
 * What the card is about, drawn as two things and the question between them: what the run read (left) and what it
 * relates to (right). `same`: the right is the same thing already held elsewhere (is this bill the one in Xero?).
 * `creates`: Accept would make the right, which does not exist yet. `changes`: Accept changes the right.
 */
export const workflowReviewPairSchema = z.object({
    relation: z.enum(["same", "creates", "changes"]),
    /** The question between them, a few words: "Same bill?". */
    question: boundedText(60),
    left: workflowReviewPairSideSchema,
    right: workflowReviewPairSideSchema,
}).strict();
/**
 * What a card can ask Twin to do that its workflow can't (send or draft an email, ask the person something, look
 * further): a session Twin starts as the person, its prompt filled in for them to change. Its facts carry the marks of
 * the card's first finding's `recordFields`, and a figure worked out says how, as a reason's do.
 */
export const workflowReviewAskSchema = z.object({
    /** The line above the composer, naming what Twin would do: "Ask Twin to send it". */
    verb: boundedText(60),
    /** What Twin is asked to do, in the person's words: "Send this reminder to Northwind Traders from my Gmail:". */
    prompt: z.array(workflowReasonSegmentSchema).min(1).max(12),
    /** Something drafted for it, quoted under the prompt: the email to send. */
    draft: z.array(workflowReasonSegmentSchema).min(1).max(24).optional(),
    /** Choices to toggle before sending: each adds a line to the prompt, or says the prompt instead. */
    options: z.array(z.object({
        label: boundedText(60),
        /** A line added under the prompt: "Cover all 4 unpaid invoices in this one email." */
        adds: boundedText(300).optional(),
        /** The prompt said instead: "Put this reminder in my Gmail drafts, to Northwind Traders. Don't send it:". */
        instead: boundedText(300).optional(),
    }).strict().refine((option) => Boolean(option.adds) !== Boolean(option.instead), "An option adds a line or says the prompt instead"))
        .max(4).optional(),
    /**
     * Sending it decides the card, because the workflow can't act on it: accepting runs the prompt as a session owned by
     * the person (the card's `onAccept` is `asks`). Absent, Ask starts a session and the card still waits to be decided.
     */
    decides: z.boolean().optional(),
    /** The prompt as the person sent it, set when sending decided the card. */
    sent: boundedText(2_000).optional(),
}).strict();
/**
 * An Ask's prompt as it is sent, with the options chosen (by their place in `options`): the prompt, or the one an
 * option says instead; then its draft quoted, each line led by "> "; then each line an option adds.
 */
export function workflowAskText(ask, chosen = []) {
    const options = (ask.options ?? []).filter((_, index) => chosen.includes(index));
    const prompt = options.find((option) => option.instead)?.instead ?? ask.prompt.map((segment) => segment.text).join("").trim();
    const draft = ask.draft?.map((segment) => segment.text).join("").trim();
    return [prompt, draft ? draft.split("\n").map((line) => `> ${line}`.trimEnd()).join("\n") : null,
        ...options.flatMap((option) => (option.adds ? [option.adds] : []))].filter(Boolean).join("\n\n");
}
export const twinWorkflowReviewPayloadSchema = z.object({
    workflowId: identifierSchema,
    activationId: identifierSchema,
    taskRunId: identifierSchema,
    channelId: identifierSchema,
    datasetId: identifierSchema,
    /**
     * The channel object type the item's record lives in, whose fields give a queue its columns' labels and types; set
     * by the engine (absent on older cards).
     */
    objectTypeId: identifierSchema.optional(),
    itemId: identifierSchema,
    inputRevision: identifierSchema,
    postingIntent: z.object({ integrationId: identifierSchema, bill: workflowXeroBillSchema }).strict().optional(),
    title: boundedText(300),
    /**
     * The item's own name, as the channel's views showed it to the run, set by the engine (absent on older cards and
     * on items without a title); `title` is the card's ask.
     */
    subject: boundedText(300).optional(),
    summary: z.string().trim().max(2000),
    /** The card's pair, where the workflow drew one (absent on cards raised before it could). */
    pair: workflowReviewPairSchema.optional(),
    /** What the card can ask Twin to do that the workflow can't, where the workflow wrote one. */
    ask: workflowReviewAskSchema.optional(),
    // A finding's judgments: its judge steps' (15 at most), the authored questions about it (16), and a second
    // reading of it whole or of each item of its list (24 at most).
    findings: z.array(workflowReviewDraftFindingSchema.extend({
        judgments: z.array(workflowFindingJudgmentSchema).max(64).optional()
    }).strict()).min(1).max(100),
    /**
     * What accepting the card does, set by the engine when it raises the card (absent on cards raised before release
     * 18): `records` only records the decision, writing and sending nothing; `continues` resumes the run, whose later
     * steps are `continuesWith`, their labels in order (absent when the review is the run's last step); `writes` sets
     * `recordWrite`'s values on the record the run read, as the person accepting; `asks` runs the card's `ask`, as the
     * person sent it, as a session they own.
     */
    onAccept: z.enum(["records", "continues", "writes", "asks"]).optional(),
    continuesWith: z.array(boundedText(120)).min(1).max(64).optional(),
    recordWrite: workflowRecordWriteSchema.optional(),
}).strict();
/** The settings a revision of a widget may change, as a card names them. */
export const CHANNEL_WIDGET_CHANGE_SETTINGS = ["Title", "Type", "Data set", "Measure", "Grouped by", "Period", "Filter", "Display"];
/**
 * A member's draft change to a channel's widgets, for an organisation admin to accept or decline
 * (docs/specs/shared-widget-blocks.md, section 4). What it stores is what the draft says, never what
 * the widgets show: a card's pictures are drawn when it is opened, as the admin opening it may see them.
 */
export const twinChannelWidgetChangePayloadSchema = z.object({
    channelId: identifierSchema,
    changesetId: identifierSchema,
    /** The change in its author's words, which the card is titled with. */
    title: boundedText(500),
    /** Who drafted it: the member whose run made the change. */
    authorId: identifierSchema,
    /** The drafter's session, where they asked for the change. */
    sessionId: identifierSchema.optional(),
    /** In the order the change lists them: the widgets it creates, then the ones it revises. */
    widgets: z.array(z.object({
        widgetId: identifierSchema,
        /** The published version a revision was made against; null for a widget the draft creates. */
        revises: identifierSchema.nullable(),
        title: boundedText(200),
        /** The widget's type in the catalogue. */
        type: identifierSchema,
        /** The data set it reads. */
        objectTypeId: identifierSchema,
        /**
         * A revision's changed settings, in words; none for a widget the draft creates. A data set is never named here:
         * its before and after are empty, and the card names it as its reader may see it.
         */
        changes: z.array(z.object({
            setting: z.enum(CHANNEL_WIDGET_CHANGE_SETTINGS),
            before: z.string().trim().max(300),
            after: z.string().trim().max(300),
        }).strict()).max(20).default([]),
    }).strict()).min(1).max(40),
    /** Where it shows once accepted, in words. */
    lands: boundedText(200),
}).strict();
export const twinReceiptSchema = z.object({
    status: twinReceiptStatusSchema,
    detail: z.record(z.string(), z.unknown()),
    at: z.number().int().nonnegative(),
}).strict();
/*
 * Shared review (docs/specs/shared-review-reviewers.md): a card can have several reviewers; any of them, or an admin,
 * may decide it, the first decision settles it, and others its source checks allow may read it. These fields are
 * optional on the schemas below so a reader of an older server still parses; the server always sends them.
 */
/** One of the people a card is for. A card with none is open to anyone its source checks allow. */
export const twinProposalReviewerSchema = z.object({ userId: identifierSchema }).strict();
/** The viewer's own relation to a card. `canDecide` is computed on the server; false means read-only. */
export const twinProposalViewerSchema = z.object({
    /** The viewer is one of the card's reviewers. */
    reviewer: z.boolean(),
    /** The viewer chose "Later"; the card stays open for the other reviewers. */
    later: z.boolean(),
    canDecide: z.boolean(),
}).strict();
/** Who decided, as they were named when they decided, and whether as a listed reviewer or an admin. */
export const twinDecisionAttributionSchema = z.object({
    actorName: z.string().trim().min(1).max(200).nullable(),
    /** Null when the card has no reviewers. */
    decidedAs: z.enum(["reviewer", "admin"]).nullable(),
}).strict();
/** The viewer's own share of a batch, beside its totals: what is left for them to decide, and what they put off. */
export const twinBatchMineSchema = z.object({
    toReview: z.number().int().nonnegative(),
    later: z.number().int().nonnegative(),
}).strict();
/**
 * Where a card's discussion lives: on the thing the review is about, so it outlives the card. A workflow card's is
 * the channel record's thread; a match card's is the matched pair's, which has no channel.
 */
export const twinProposalDiscussionSchema = z.object({
    subjectType: z.enum(["record", "entity_match"]),
    subjectId: z.string().trim().min(1).max(600),
    channelId: identifierSchema.nullable(),
    commentCount: z.number().int().nonnegative(),
}).strict();
export const twinDecisionSchema = z.object({
    id: identifierSchema,
    proposalId: identifierSchema,
    decision: twinDecisionKindSchema,
    /** The matcher outcome a link decision chose; null for the other kinds. */
    outcomeId: slugSchema.nullable(),
    outcomeClass: matchOutcomeClassSchema.nullable(),
    actor: identifierSchema,
    note: z.string().trim().max(4_000).nullable(),
    decidedAt: z.number().int().nonnegative(),
    sourceVersion: z.number().int().positive().nullable(),
    /** Null means the historical evidence was not captured and is unavailable. */
    observationId: identifierSchema.nullable().default(null),
    snapshotSha256: sha256Schema,
    /** The latest receipt of what the decision did; null when nothing has run yet. */
    receipt: twinReceiptSchema.nullable(),
    /** Set once the decision was undone; the proposal went back to open. */
    reversal: z.object({
        actor: identifierSchema,
        note: z.string().trim().max(4_000).nullable(),
        at: z.number().int().nonnegative(),
    }).strict().nullable(),
    actorName: twinDecisionAttributionSchema.shape.actorName.optional(),
    decidedAs: twinDecisionAttributionSchema.shape.decidedAs.optional(),
}).strict();
const proposalBase = z.object({
    id: identifierSchema,
    batchId: identifierSchema,
    sourceId: identifierSchema,
    sourceLabel: boundedText(200),
    sourceSlug: slugSchema.nullable(),
    sourceVersion: z.number().int().positive().nullable(),
    /** Addressed to one person; null when the whole organisation may decide. */
    recipientUserId: identifierSchema.nullable(),
    subjectKey: z.string().trim().min(1).max(512),
    title: z.string().trim().min(1).max(600),
    confidence: z.number().min(0).max(1).nullable(),
    band: twinProposalBandSchema,
    state: twinProposalStateSchema,
    effectClass: twinEffectClassSchema,
    /** Other open proposals from the same source that claim one of this proposal's records. */
    contested: z.number().int().nonnegative().default(0),
    /** Null means the historical evidence was not captured and is unavailable. */
    observationId: identifierSchema.nullable().default(null),
    snapshotSha256: sha256Schema,
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    /** The decision in force, if any. */
    decision: twinDecisionSchema.nullable(),
    /** The people the card is for; empty when it is open to anyone allowed. */
    reviewers: z.array(twinProposalReviewerSchema).max(10).optional(),
    /**
     * Whether reviewers were listed for the card. True with no reviewers means none was available when it landed:
     * only an admin decides it.
     */
    reviewersListed: z.boolean().optional(),
    viewer: twinProposalViewerSchema.optional(),
    /** Where the card's discussion lives; null for a kind without one. */
    discussion: twinProposalDiscussionSchema.nullable().optional(),
}).strict();
export const twinProposalSchema = z.discriminatedUnion("kind", [
    proposalBase.extend({ kind: z.literal("link"), payload: twinLinkPayloadSchema }).strict(),
    proposalBase.extend({ kind: z.literal("definition"), payload: twinDefinitionPayloadSchema }).strict(),
    proposalBase.extend({ kind: z.literal("action"), payload: twinActionPayloadSchema }).strict(),
    proposalBase.extend({ kind: z.literal("workflow_review"), payload: twinWorkflowReviewPayloadSchema }).strict(),
    proposalBase.extend({ kind: z.literal("channel_widget_change"), payload: twinChannelWidgetChangePayloadSchema }).strict(),
]);
export const twinBatchCountsSchema = z.object({
    open: z.number().int().nonnegative(),
    /** Open proposals that carry a contradiction. */
    contradictions: z.number().int().nonnegative(),
    /** Automatic-band proposals left open because the policy or stale data held them. */
    held: z.number().int().nonnegative(),
    deferred: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    declined: z.number().int().nonnegative(),
    /** Decisions in force the engine made on its own. */
    automatic: z.number().int().nonnegative(),
    reversed: z.number().int().nonnegative(),
}).strict();
export const twinBatchSchema = z.object({
    id: identifierSchema,
    kind: twinProposalKindSchema,
    sourceId: identifierSchema,
    sourceLabel: boundedText(200),
    /** What the source is for, in its own words (a workflow's description), for a queue to show under its name. */
    sourceDescription: z.string().max(500).nullable().optional(),
    sourceSlug: slugSchema.nullable(),
    sourceVersion: z.number().int().positive().nullable(),
    recipientUserId: identifierSchema.nullable(),
    /** queued: asked for and waiting for its engine; running: the engine has it. */
    status: z.enum(["queued", "running", "succeeded", "failed"]),
    error: z.string().nullable(),
    startedAt: z.number().int().nonnegative(),
    finishedAt: z.number().int().nonnegative().nullable(),
    snapshotSha256: sha256Schema.nullable(),
    /** The engine's own run statistics; shape depends on the kind. */
    stats: z.record(z.string(), z.unknown()),
    freshness: matchRunFreshnessSchema.nullable(),
    stale: z.boolean(),
    /** The people the batch's cards are for, when they list reviewers. */
    reviewerUserIds: z.array(identifierSchema).max(64).optional(),
    mine: twinBatchMineSchema.optional(),
    counts: twinBatchCountsSchema,
}).strict();
export const twinPolicySchema = z.object({
    kind: twinProposalKindSchema,
    /** ask: every proposal waits for a person; auto: the engine's automatic band may act. */
    mode: z.enum(TWIN_POLICY_MODES),
    acceptRole: z.enum(TWIN_ACCEPT_ROLES),
    /** When set, a proposal at or above this confidence is the automatic band, whatever the engine said. */
    automaticThreshold: z.number().min(0).max(1).nullable(),
    /** How many proposals the engine may raise per day; null for no cap. */
    dailyCap: z.number().int().min(0).max(1_000).nullable(),
    settings: z.record(z.string(), z.unknown()),
    updatedAt: z.number().int().nonnegative().nullable(),
}).strict();
/** What the calling person has chosen for background action sensing. */
export const twinActionSubscriptionSchema = z.object({
    backgroundEnabled: z.boolean(),
    dailyCap: z.number().int().min(0).max(20),
    backgroundAvailable: z.boolean(),
}).strict();
export const twinPoliciesResponseSchema = z.object({
    policies: z.array(twinPolicySchema.extend({ kind: z.enum(TWIN_CONFIGURABLE_POLICY_KINDS) }).strict()).length(TWIN_CONFIGURABLE_POLICY_KINDS.length),
    subscription: twinActionSubscriptionSchema,
    canManage: z.boolean(),
}).strict();
export const updateTwinPolicyRequestSchema = z.object({
    kind: z.enum(TWIN_CONFIGURABLE_POLICY_KINDS),
    mode: z.enum(TWIN_POLICY_MODES).optional(),
    acceptRole: z.enum(TWIN_ACCEPT_ROLES).optional(),
    automaticThreshold: z.number().min(0).max(1).nullable().optional(),
    dailyCap: z.number().int().min(0).max(1_000).nullable().optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    /** Only meaningful for actions; a person's own background sensing. */
    subscription: z.object({
        backgroundEnabled: z.boolean().optional(),
        dailyCap: z.number().int().min(0).max(20).optional(),
    }).strict().optional(),
}).strict();
export const listTwinProposalsQuerySchema = z.object({
    kind: twinProposalKindSchema.optional(),
    batchId: identifierSchema.optional(),
    state: twinProposalStateSchema.default("open"),
    band: twinProposalBandSchema.optional(),
    minConfidence: z.coerce.number().min(0).max(1).optional(),
    maxConfidence: z.coerce.number().min(0).max(1).optional(),
    contradictions: z.enum(["any", "none"]).optional(),
    contested: z.enum(["any", "none"]).optional(),
    /** The viewer's own Later. With state=open the list leaves those cards out unless asked for only them. */
    later: z.enum(["exclude", "only"]).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().trim().min(1).max(600).optional(),
}).strict();
export const listTwinProposalsResponseSchema = z.object({
    proposals: z.array(twinProposalSchema),
    nextCursor: z.string().nullable(),
}).strict();
export const workflowReviewEvidenceSchema = z.object({
    sources: z.array(z.object({ ref: z.string(), sourceKey: z.string(), externalId: z.string(),
        observedRevision: z.string(), currentRevision: z.string().nullable(),
        status: z.enum(["current", "changed", "missing", "unavailable"]),
        record: z.record(z.string(), z.json()).nullable(), detail: z.string().nullable(),
        /**
         * The read's selected fields as the source names them, so a review can label a marked field. An amount of money
         * the source declares is typed `currency`, with the ISO currency where its own record names it.
         */
        fields: z.array(z.object({ key: z.string(), label: z.string().nullable(), type: z.string(),
            currency: z.string().regex(/^[A-Z]{3}$/u).optional() }).strict())
            .max(64).optional(),
        /** On an accepted card's written record that reads as changed: the change is exactly the card's own write. */
        changedOnlyByThisCard: z.literal(true).optional(),
    }).strict()).max(256),
    documents: z.array(z.object({ ref: z.string(), fileId: z.string(),
        sourceHash: z.string(), currentHash: z.string().nullable(), evidence: z.json(),
    }).strict()).max(20),
    reviews: z.array(z.object({ ref: z.string(), proposalId: z.string(),
        decision: twinDecisionSchema.nullable(), available: z.boolean(),
    }).strict()).max(40),
    /**
     * The reviewed record's own fields, as its workflow declares them: `value`
     * is what the record holds now, `before` what it held when the run that
     * raised this review started. A workflow may write a value before asking
     * for sign-off, so `before` is the baseline a proposal changes;
     * `baselineAvailable` is false for runs that predate retained input. Fields
     * read from connected sources and the review's own state columns are left
     * out; the record's channel shows them. Null when the record is gone.
     */
    record: z.object({
        /** The channel record a reviewer edits to correct it, and the version the record API serves for it. */
        channelId: z.string(),
        recordId: z.string(),
        version: z.number().int().positive().nullable(),
        title: z.string().nullable(),
        baselineAvailable: z.boolean(),
        /** The reviewer-owned json field that holds corrections ({ document?, fields? }), when the dataset declares one. */
        correctionsFieldKey: z.string().nullable(),
        fields: z.array(z.object({ key: z.string(), label: z.string(), type: channelObjectFieldTypeSchema,
            scope: z.enum(["item", "line"]), owner: z.enum(["workflow", "reviewer"]), value: z.json().nullable(),
            before: z.json().nullable(),
            /** The field's display configuration as the channel table applies it: currency, precision, options. */
            config: z.record(z.string(), z.json()).nullable(),
        }).strict()).max(200),
    }).strict().nullable(),
    /**
     * What Twin Judge did for this review. `judgeModel` is the activation's pinned model (null: none, so nothing
     * here was judged). The second reader `read` the review, `failed` (detail says why), could not run because
     * the model is `unpinned`, or is `off` for this organisation.
     */
    checks: z.object({
        judgeModel: z.string().nullable(),
        secondReader: z.object({ status: z.enum(["read", "failed", "unpinned", "off"]),
            detail: z.string().max(300).nullable() }).strict(),
    }).strict().optional(),
    /** The item has run again since this review, after a correction for example, and that run failed. */
    failure: workflowItemFailureSchema.nullable().optional(),
    /**
     * The counterparty's history the run read: one entry per read that found an established pattern or looked for
     * one, accepted sign-offs first. A judgment's `patternKey` names the entry it answered about.
     */
    history: z.array(workflowHistoryEntrySchema).max(16).optional(),
}).strict();
/**
 * A widget draft as its card's reader sees it when they open it: computed then, under their own access,
 * and never stored. A data set they may not read has no name, and its widgets' results are "denied".
 */
export const channelWidgetDraftDetailSchema = z.object({
    status: z.enum(["draft", "stale", "published", "declined"]),
    /** Why the draft cannot be accepted as it stands: a widget it revises has been changed since. */
    stale: z.object({
        widgetId: identifierSchema,
        title: boundedText(200),
        changedBy: z.object({ userId: identifierSchema, name: z.string().trim().max(200).nullable() }).strict(),
        changedAt: z.number().int().nonnegative(),
    }).strict().nullable(),
    preview: z.object({
        evaluatedAt: z.number().int().nonnegative(),
        timezone: z.string().min(1).max(64),
        /** The draft's widgets as specs, each with its data set's name where the reader may read it. */
        widgets: z.array(channelArrangedWidgetSchema.extend({ dataSetName: z.string().trim().max(200).nullable() }).strict()).max(40),
        results: z.record(z.string().min(1), widgetResultSchema(z.unknown())),
    }).strict(),
    /** The Overview's published version, for Accept and place to be made against; null when it has none. */
    overviewVersionId: identifierSchema.nullable(),
}).strict();
export const getTwinProposalResponseSchema = z.object({
    proposal: twinProposalSchema,
    batch: twinBatchSchema,
    decisions: z.array(twinDecisionSchema),
    observations: z.array(twinProposalObservationSchema),
    workflowEvidence: workflowReviewEvidenceSchema.optional(),
    channelWidgetDraft: channelWidgetDraftDetailSchema.optional(),
}).strict();
export const decideTwinProposalRequestSchema = z.object({
    decision: z.enum(["accept", "decline", "later"]),
    /** For links: which of the matcher's outcomes; defaults to its accepting or declining outcome. */
    outcomeId: slugSchema.optional(),
    note: z.string().trim().max(4_000).optional(),
    /** For actions, and workflow cards whose Ask decides them: accept with the prompt as the person sent it. */
    prompt: z.string().trim().min(1).max(2_000).optional(),
    /**
     * For a widget change: accept and place its new widgets on the channel's Overview, made against the Overview's
     * published version the person saw (null when it had none).
     */
    place: z.object({ surface: z.literal("overview"), expectedVersionId: z.string().trim().min(1).max(200).nullable() }).strict().optional(),
}).strict();
export const decideTwinProposalResponseSchema = z.object({
    proposal: twinProposalSchema,
}).strict();
export const undoTwinProposalRequestSchema = z.object({
    note: z.string().trim().max(4_000).optional(),
}).strict();
export const retireTwinProposalRequestSchema = z.object({
    note: z.string().trim().max(4_000).optional(),
}).strict();
export const TWIN_BULK_ACTIONS = ["accept", "decline", "later", "reopen", "undo"];
export const bulkTwinProposalsRequestSchema = z.object({
    proposalIds: z.array(identifierSchema).min(1).max(200),
    action: z.enum(TWIN_BULK_ACTIONS),
    outcomeId: slugSchema.optional(),
    note: z.string().trim().max(4_000).optional(),
}).strict();
export const twinBulkResultSchema = z.object({
    proposalId: identifierSchema,
    status: z.enum(["accepted", "declined", "deferred", "reopened", "undone", "skipped"]),
    reason: z.string().optional(),
}).strict();
export const bulkTwinProposalsResponseSchema = z.object({
    results: z.array(twinBulkResultSchema),
    accepted: z.number().int().nonnegative(),
    declined: z.number().int().nonnegative(),
    deferred: z.number().int().nonnegative(),
    reopened: z.number().int().nonnegative(),
    undone: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
}).strict();
export const listTwinBatchesQuerySchema = z.object({
    kind: twinProposalKindSchema.optional(),
    /** open: batches with proposals still waiting; all: every batch. */
    view: z.enum(["open", "all"]).default("all"),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).strict();
export const listTwinBatchesResponseSchema = z.object({
    batches: z.array(twinBatchSchema),
}).strict();
/**
 * How a workflow's review confidence has matched people's decisions: over its decided reviews that carry a
 * confidence (accepted is "right", declined is "wrong"), the share Twin Judge got right at 0.5, and per threshold
 * the share of reviews at or above it that were accepted (precision) and of accepted reviews it would take
 * (coverage). `recommended` is the lowest threshold with precision of at least 0.95 over at least 20 reviews.
 */
export const workflowJudgeCalibrationSchema = z.object({
    model: z.string().nullable(),
    decided: z.number().int().nonnegative(),
    agreement: z.number().min(0).max(1).nullable(),
    thresholds: z.array(z.object({ threshold: z.number().min(0).max(1),
        precision: z.number().min(0).max(1).nullable(), coverage: z.number().min(0).max(1).nullable(),
        cards: z.number().int().nonnegative() }).strict()).max(20),
    recommended: z.number().min(0).max(1).nullable(),
}).strict();
export const getTwinBatchResponseSchema = z.object({ batch: twinBatchSchema,
    /** For a workflow review batch whose workflow's reviews carry a confidence. */
    calibration: workflowJudgeCalibrationSchema.nullable().optional(),
    /** Items of a workflow review batch's workflow whose latest run failed, so they raised no open review; newest first. */
    failures: z.array(workflowItemFailureSchema).max(50).optional() }).strict();
/** Accept every open proposal in a batch, optionally only those at or above a confidence. */
export const acceptTwinBatchRequestSchema = z.object({
    minConfidence: z.number().min(0).max(1).optional(),
    note: z.string().trim().max(4_000).optional(),
}).strict();
export const acceptTwinBatchResponseSchema = z.object({
    batch: twinBatchSchema,
    results: z.array(twinBulkResultSchema),
    accepted: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
}).strict();
/** Ask an engine to propose now: a matcher run, or a review of recent activity for actions. */
export const runTwinBatchRequestSchema = z.object({
    kind: z.enum(["link", "action"]),
    /** The matcher to run; required for links. */
    sourceId: identifierSchema.optional(),
}).strict().refine((value) => value.kind !== "link" || value.sourceId !== undefined, {
    message: "a link batch names the matcher to run",
    path: ["sourceId"],
});
export const runTwinBatchResponseSchema = z.object({
    /** completed: the batch ran in this request; dispatched or queued: an engine job will land it. */
    status: z.enum(["completed", "dispatched", "queued", "disabled", "cap_reached", "nothing_new"]),
    batch: twinBatchSchema.nullable(),
    jobId: z.string().nullable(),
}).strict();
/** One line of the ledger: a decision or its reversal, with enough of the proposal to read it. */
export const twinChangeSchema = z.object({
    id: identifierSchema,
    kind: twinProposalKindSchema,
    proposalId: identifierSchema,
    proposalTitle: z.string().trim().min(1).max(600),
    batchId: identifierSchema,
    sourceLabel: boundedText(200),
    decision: twinDecisionKindSchema,
    actor: identifierSchema,
    automatic: z.boolean(),
    note: z.string().trim().max(4_000).nullable(),
    at: z.number().int().nonnegative(),
    receipt: twinReceiptSchema.nullable(),
    reversal: twinDecisionSchema.shape.reversal,
    /** True when the decision is in force and its effect can be put back. */
    undoable: z.boolean(),
}).strict();
export const listTwinChangesQuerySchema = z.object({
    kind: twinProposalKindSchema.optional(),
    actor: identifierSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().trim().min(1).max(600).optional(),
}).strict();
export const listTwinChangesResponseSchema = z.object({
    changes: z.array(twinChangeSchema),
    nextCursor: z.string().nullable(),
}).strict();
/**
 * What an engine hands the control plane when a batch has been scored: the
 * batch's own facts and every proposal it raised. The control plane lands it:
 * starts or fulfils the batch, upserts the proposals, applies the policy to
 * the automatic band, and tells the reviewers. The link engine (the connector
 * runner) uses this; the action proposer has its own write-back shape.
 */
export const twinLinkBatchLandingSchema = z.object({
    tenantSlug: boundedText(120),
    organisationId: identifierSchema,
    /** Stable across delivery retries; queued runs retain the batch the user requested. */
    batchId: identifierSchema,
    sourceId: identifierSchema,
    sourceLabel: boundedText(200),
    sourceVersion: z.number().int().positive(),
    snapshotSha256: sha256Schema,
    status: z.enum(["succeeded", "failed"]),
    error: z.string().max(4_000).nullable().default(null),
    stats: z.record(z.string(), z.unknown()),
    freshness: matchRunFreshnessSchema,
    proposals: z.array(z.object({
        subjectKey: z.string().trim().min(1).max(512),
        sourceRevisions: twinSourceRevisionsSchema,
        payload: twinLinkPayloadSchema,
        confidence: z.number().min(0).max(1),
        band: twinProposalBandSchema,
        state: z.enum(["open", "deferred"]).default("open"),
    }).strict()).max(50_000),
}).strict();
export const twinLinkBatchLandingResponseSchema = z.object({
    batchId: identifierSchema,
    proposals: z.number().int().nonnegative(),
    /** Automatic-band proposals the policy let the engine decide, and those it held. */
    decided: z.number().int().nonnegative(),
    held: z.number().int().nonnegative(),
    materialised: z.number().int().nonnegative(),
    materialisationFailures: z.number().int().nonnegative(),
}).strict();
/** A short human title for a proposal, the same everywhere it is listed. */
export function twinProposalTitle(input) {
    if (input.kind === "link") {
        const payload = input.payload;
        const name = (ref) => ref.title?.trim() || ref.externalId;
        return `${name(payload.left)} ↔ ${name(payload.right)}`;
    }
    if (input.kind === "definition") {
        const payload = input.payload;
        return `${payload.productSlug.replaceAll("_", " ")} v${payload.productVersion}`;
    }
    return input.payload.title;
}
